// pages/overlay/TitleCardGTS.jsx
// Supporting title card #1 — Golden Ticket System feature label.
// Lower-third pill overlay. Mirrors the hero TitleCard pattern:
//   /overlay/titlecard-gts          — preview, auto-loops
//   /overlay/titlecard-gts?render   — frame-by-frame WebM download
//   ?bg=green                       — chroma-key background mode
//
// Animation arc (3.5s):
//   0.0–0.5s  card fades + translates up + slight scale-up
//   0.5–3.0s  held
//   3.0–3.5s  fades out with slight downward drift

import { useEffect, useRef, useState } from "react";

const W = 1080;
const H = 1920;
const FPS = 60;
const TOTAL_MS = 3500;
const TOTAL_FRAMES = Math.ceil((TOTAL_MS / 1000) * FPS);

// Brand palette
const NAVY   = "#1d52a0";
const ORANGE = "#f57600";
const GOLD   = "#fcb31f";
const CREAM  = "#ffdaad";

// Pill placement — lower third
const PILL_CX = W / 2;
const PILL_CY = 1500;
const PILL_W  = 920;
const PILL_H  = 240;

// Timing (ms)
const IN_DUR     = 500;
const OUT_START  = 3000;
const OUT_DUR    = 500;

// Corner sparkles (inside cream interior, near corners)
const SPARKLE_INSET_X = 80;
const SPARKLE_INSET_Y = 50;
const SPARKLES = [
  { x: PILL_CX - PILL_W / 2 + SPARKLE_INSET_X, y: PILL_CY - PILL_H / 2 + SPARKLE_INSET_Y, size: 20, phase: 0.0 },
  { x: PILL_CX + PILL_W / 2 - SPARKLE_INSET_X, y: PILL_CY - PILL_H / 2 + SPARKLE_INSET_Y, size: 20, phase: 0.6 },
  { x: PILL_CX - PILL_W / 2 + SPARKLE_INSET_X, y: PILL_CY + PILL_H / 2 - SPARKLE_INSET_Y, size: 20, phase: 1.2 },
  { x: PILL_CX + PILL_W / 2 - SPARKLE_INSET_X, y: PILL_CY + PILL_H / 2 - SPARKLE_INSET_Y, size: 20, phase: 1.8 },
];

// ── EASING ───────────────────────────────────────────────────────────────────
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

// ── HELPERS ──────────────────────────────────────────────────────────────────
function roundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function drawSparkle(ctx, cx, cy, size, color, alpha) {
  if (alpha <= 0.02) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = color;
  ctx.shadowColor = color;
  ctx.shadowBlur = size * 0.6;
  ctx.beginPath();
  ctx.moveTo(cx,                cy - size);
  ctx.lineTo(cx + size * 0.20,  cy - size * 0.20);
  ctx.lineTo(cx + size,         cy);
  ctx.lineTo(cx + size * 0.20,  cy + size * 0.20);
  ctx.lineTo(cx,                cy + size);
  ctx.lineTo(cx - size * 0.20,  cy + size * 0.20);
  ctx.lineTo(cx - size,         cy);
  ctx.lineTo(cx - size * 0.20,  cy - size * 0.20);
  ctx.closePath();
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.fillStyle = `rgba(255, 240, 210, ${alpha * 0.85})`;
  ctx.beginPath();
  ctx.arc(cx, cy, size * 0.16, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

// ── PILL ─────────────────────────────────────────────────────────────────────
// Cream interior with gold outer trim and inset navy hairline. Drop shadow
// grounds it. Reads as a vintage admission ticket.
function drawPill(ctx) {
  const x = PILL_CX - PILL_W / 2;
  const y = PILL_CY - PILL_H / 2;
  const pillR = PILL_H / 2;

  // Drop shadow + gold outer
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.35)";
  ctx.shadowBlur = 30;
  ctx.shadowOffsetY = 12;
  ctx.fillStyle = GOLD;
  roundedRect(ctx, x, y, PILL_W, PILL_H, pillR);
  ctx.fill();
  ctx.restore();

  // Cream interior (inset 8px from gold edge)
  ctx.fillStyle = CREAM;
  roundedRect(ctx, x + 8, y + 8, PILL_W - 16, PILL_H - 16, pillR - 8);
  ctx.fill();

  // Thin navy hairline inset further (10px deeper)
  ctx.strokeStyle = "rgba(29,82,160,0.4)";
  ctx.lineWidth = 2;
  roundedRect(ctx, x + 18, y + 18, PILL_W - 36, PILL_H - 36, pillR - 18);
  ctx.stroke();
}

// ── TEXT ─────────────────────────────────────────────────────────────────────
function drawText(ctx) {
  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";

  // Headline — Chango all caps, navy
  ctx.font = "400 42px 'Chango', serif";
  ctx.fillStyle = NAVY;
  ctx.fillText("THE GOLDEN TICKET SYSTEM", PILL_CX, PILL_CY - 4);

  // Subhead — Fredoka 600 all caps, letter-spaced editorial tag.
  // Sans-serif handles short-duration scannable copy far better than script.
  // Split into three parts so the bullet can be orange like the hero card.
  ctx.font = "600 22px Fredoka, sans-serif";
  ctx.letterSpacing = "3px";
  const partA = "A DIGITAL KEEPSAKE PORTAL";
  const partB = "·";
  const partC = "FOR EVERY GUEST";
  ctx.textAlign = "left";
  const gap = ctx.measureText("  ").width;
  const wA = ctx.measureText(partA).width;
  const wB = ctx.measureText(partB).width;
  const wC = ctx.measureText(partC).width;
  const totalW = wA + gap + wB + gap + wC;
  const x0 = PILL_CX - totalW / 2;
  const xB = x0 + wA + gap;
  const xC = xB + wB + gap;
  const y  = PILL_CY + 48;

  ctx.fillStyle = NAVY;
  ctx.fillText(partA, x0, y);
  ctx.fillStyle = ORANGE;
  ctx.fillText(partB, xB, y);
  ctx.fillStyle = NAVY;
  ctx.fillText(partC, xC, y);

  ctx.letterSpacing = "0px";
  ctx.restore();
}

// ── FRAME DRAWING ────────────────────────────────────────────────────────────
function drawFrame(ctx, frameNum, bgMode) {
  const ms = (frameNum / FPS) * 1000;

  ctx.clearRect(0, 0, W, H);
  if (bgMode === "green") {
    ctx.fillStyle = "#00FF00";
    ctx.fillRect(0, 0, W, H);
  }

  // Card lifecycle
  let alpha = 1;
  let offsetY = 0;
  let scale = 1;

  if (ms < IN_DUR) {
    const p = ms / IN_DUR;
    const eased = easeOutCubic(p);
    alpha = eased;
    offsetY = (1 - eased) * 60;
    scale = 0.96 + 0.04 * eased;
  } else if (ms >= OUT_START && ms < OUT_START + OUT_DUR) {
    const p = (ms - OUT_START) / OUT_DUR;
    const eased = easeOutCubic(p);
    alpha = 1 - eased;
    offsetY = eased * 24;
  } else if (ms >= OUT_START + OUT_DUR) {
    return; // fully gone
  }

  if (alpha <= 0.001) return;

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(PILL_CX, PILL_CY + offsetY);
  ctx.scale(scale, scale);
  ctx.translate(-PILL_CX, -PILL_CY);

  drawPill(ctx);

  // Corner sparkles — gentle opacity shimmer during hold
  SPARKLES.forEach(({ x, y, size, phase }) => {
    let shimmer = 1;
    if (ms > IN_DUR && ms < OUT_START) {
      const t = (ms - IN_DUR) / 1000 + phase;
      shimmer = 0.78 + 0.22 * (0.5 + 0.5 * Math.sin(t * Math.PI * 0.66));
    }
    drawSparkle(ctx, x, y, size, GOLD, shimmer);
  });

  drawText(ctx);

  // Tiny orange accent dot on left and right inside the pill
  ctx.fillStyle = ORANGE;
  ctx.beginPath();
  ctx.arc(PILL_CX - PILL_W / 2 + 36, PILL_CY, 4, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(PILL_CX + PILL_W / 2 - 36, PILL_CY, 4, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

// ── ASSET LOADING ────────────────────────────────────────────────────────────
async function ensureFontsLoaded() {
  if (!document.fonts) return;
  await Promise.all([
    document.fonts.load("400 42px 'Chango'"),
    document.fonts.load("600 22px Fredoka"),
  ]);
}

// ── PREVIEW MODE ─────────────────────────────────────────────────────────────
function PreviewMode({ bgMode }) {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => { ensureFontsLoaded().then(() => setReady(true)); }, []);

  useEffect(() => {
    if (!ready) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let frame = 0;
    let raf;
    function tick() {
      drawFrame(ctx, frame, bgMode);
      frame++;
      if (frame > TOTAL_FRAMES + 60) frame = 0; // loop with pause
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [ready, bgMode]);

  return (
    <div style={{ width: "100vw", height: "100vh", background: "#1a1a1a", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
      <canvas ref={canvasRef} width={W} height={H} style={{ maxWidth: "100%", maxHeight: "100%", boxShadow: "0 8px 40px rgba(0,0,0,0.5)" }} />
      <div style={{ position: "absolute", bottom: "1rem", left: "50%", transform: "translateX(-50%)", color: "rgba(255,255,255,0.55)", fontSize: "0.85rem", fontFamily: "system-ui", textAlign: "center", lineHeight: 1.5 }}>
        GTS Card · bg={bgMode} · auto-loops<br />
        <span style={{ opacity: 0.7 }}>?render to download · ?bg=green for chroma-key</span>
      </div>
    </div>
  );
}

// ── RENDER MODE ──────────────────────────────────────────────────────────────
function RenderMode({ bgMode }) {
  const [status, setStatus] = useState("Loading fonts...");
  const canvasRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await ensureFontsLoaded();
      if (cancelled) return;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      setStatus("Rendering frames...");
      const stream = canvas.captureStream(0);
      const recorder = new MediaRecorder(stream, {
        mimeType: "video/webm;codecs=vp9",
        videoBitsPerSecond: 12_000_000,
      });
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.start();

      for (let f = 0; f <= TOTAL_FRAMES + 5; f++) {
        drawFrame(ctx, f, bgMode);
        const track = stream.getVideoTracks()[0];
        if (track.requestFrame) track.requestFrame();
        await new Promise((r) => setTimeout(r, 1000 / FPS));
        if (f % 30 === 0) setStatus(`Rendering frame ${f}/${TOTAL_FRAMES}...`);
      }

      ctx.clearRect(0, 0, W, H);
      const track = stream.getVideoTracks()[0];
      if (track.requestFrame) track.requestFrame();
      await new Promise((r) => setTimeout(r, 100));

      recorder.stop();
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: "video/webm" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `gts-card-${bgMode}.webm`;
        a.click();
        setStatus(`Downloaded gts-card-${bgMode}.webm`);
      };
    })();
    return () => { cancelled = true; };
  }, [bgMode]);

  return (
    <div style={{ width: "100vw", height: "100vh", background: "#0d1833", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "1.5rem", fontFamily: "system-ui" }}>
      <canvas ref={canvasRef} width={W} height={H} style={{ maxHeight: "75vh", maxWidth: "90vw", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8 }} />
      <div style={{ color: GOLD, fontSize: "1.1rem", fontWeight: 700 }}>{status}</div>
    </div>
  );
}

// ── ROUTER ───────────────────────────────────────────────────────────────────
export default function TitleCardGTS() {
  const params = new URLSearchParams(window.location.search);
  const isRender = params.has("render");
  const bgMode = params.get("bg") === "green" ? "green" : "transparent";
  return isRender ? <RenderMode bgMode={bgMode} /> : <PreviewMode bgMode={bgMode} />;
}
